import React, { useRef, useState, useEffect } from 'react';

interface SmallAudioButtonProps {
  audioSrc: string;
  className?: string;
}

const SmallAudioButton: React.FC<SmallAudioButtonProps> = ({ audioSrc, className = '' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  
  // Set up intersection observer to detect when the button enters viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasEnteredView(true);
        }
      },
      { threshold: 0.5 }
    );
    
    if (buttonRef.current) {
      observer.observe(buttonRef.current);
    }
    
    return () => {
      if (buttonRef.current) {
        observer.unobserve(buttonRef.current);
      }
    };
  }, []);
  
  // Trigger animation once when the component enters the viewport
  useEffect(() => {
    if (hasEnteredView && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [hasEnteredView, hasAnimated]);
  
  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };
  
  const handlePlay = () => {
    setIsPlaying(true);
  };
  
  const handlePause = () => {
    setIsPlaying(false);
  };
  
  const handleEnded = () => {
    setIsPlaying(false);
  };
  
  return (
    <div className={`inline-flex items-center ${className}`}>
      <button 
        ref={buttonRef}
        onClick={togglePlayPause}
        // Increased size from w-6 h-6 to w-7 h-7 (about 15% bigger)
        className={`w-7 h-7 rounded-full bg-[#C2A4D6] flex items-center justify-center hover:bg-[#b390c9] transition-colors focus:outline-none focus:ring-2 focus:ring-[#C2A4D6] focus:ring-opacity-50 shadow-sm relative overflow-hidden
          ${hasEnteredView && !hasAnimated ? 'animate-pulse' : ''}`}
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {/* Initial attention animation */}
        {hasEnteredView && !hasAnimated && (
          <span className="absolute inset-0 bg-white opacity-20 animate-ping" 
                style={{ animationDuration: '1s', animationIterationCount: 2 }}
                onAnimationEnd={() => setHasAnimated(true)}></span>
        )}
        
        {/* Pulsing animation when playing */}
        <span className={`absolute inset-0 bg-white opacity-20 ${isPlaying ? 'animate-pulse' : ''}`} style={{ animationDuration: '2s' }}></span>
        
        {isPlaying ? (
          <span className="text-white z-10">
            {/* Increased icon size from 12 to 14 */}
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="4" width="4" height="16"></rect>
              <rect x="14" y="4" width="4" height="16"></rect>
            </svg>
          </span>
        ) : (
          <span className="text-white z-10">
            {/* Increased icon size from 12 to 14 */}
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          </span>
        )}
      </button>
      
      {/* Small text label */}
      <span className="text-[9px] text-[#C2A4D6] font-medium ml-1">
        {isPlaying ? "▶" : "♪"}
      </span>
      
      <audio 
        ref={audioRef}
        className="hidden"
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
      >
        <source src={audioSrc} type="audio/mp3" />
        Your browser does not support audio playback.
      </audio>
    </div>
  );
};

export default SmallAudioButton;